import React from 'react';
import { Sparkles, Image as ImageIcon, Download, Package, LogIn } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { LanguageSelector } from '../components/common';

const LoginScreen = ({ onNavigate }) => {
  const { login } = useAuth();
  const { t } = useTranslation();

  const features = [
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: t('landing.features.aiGeneration.title'),
      description: t('landing.features.aiGeneration.description')
    },
    {
      icon: <ImageIcon className="w-8 h-8" />,
      title: t('landing.features.customCanvas.title'),
      description: t('landing.features.customCanvas.description')
    },
    {
      icon: <Download className="w-8 h-8" />,
      title: t('landing.features.export.title'),
      description: t('landing.features.export.description')
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: t('landing.features.print.title'),
      description: t('landing.features.print.description')
    }
  ];

  const steps = [
    {
      number: '1',
      title: t('landing.howItWorks.step1.title'),
      description: t('landing.howItWorks.step1.description')
    },
    {
      number: '2',
      title: t('landing.howItWorks.step2.title'),
      description: t('landing.howItWorks.step2.description')
    },
    {
      number: '3',
      title: t('landing.howItWorks.step3.title'),
      description: t('landing.howItWorks.step3.description')
    },
    {
      number: '4',
      title: t('landing.howItWorks.step4.title'),
      description: t('landing.howItWorks.step4.description')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <img
              src="logo.png"
              alt={t('common.appName')}
              className="h-10 sm:h-12"
            />
            <div className="flex items-center gap-3">
              <LanguageSelector />
              <button
                onClick={login}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition text-sm sm:text-base"
              >
                {t('auth.login')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            {t('landing.hero.title')}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            {t('landing.hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={login}
              className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition flex items-center justify-center gap-2 text-lg shadow-lg hover:shadow-xl"
            >
              <Sparkles className="w-5 h-5" />
              {t('landing.hero.cta')}
            </button>
            <a
              href="#demo"
              className="w-full sm:w-auto text-gray-700 px-8 py-4 rounded-xl font-semibold border-2 border-gray-200 hover:border-purple-300 hover:bg-white transition flex items-center justify-center gap-2 text-lg"
            >
              {t('landing.hero.watchDemo')}
            </a>
          </div>
        </div>
      </section>

      {/* Video Demo Section */}
      <section id="demo" className="py-16 px-4 bg-white/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-4">
            {t('landing.demo.title')}
          </h2>
          <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
            {t('landing.demo.subtitle')}
          </p>
          <div className="relative aspect-video bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl overflow-hidden shadow-2xl">
            <video
              className="w-full h-full object-cover"
              controls
              poster="demo-thumbnail.png"
            >
              <source src="demo.mp4" type="video/mp4" />
              {t('landing.demo.videoFallback')}
            </video>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-4">
            {t('landing.features.title')}
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            {t('landing.features.subtitle')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center text-purple-600 mb-4 group-hover:scale-110 transition">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 px-4 bg-white/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-4">
            {t('landing.howItWorks.title')}
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            {t('landing.howItWorks.subtitle')}
          </p>
          <div className="space-y-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className="flex items-start gap-6"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                  {step.number}
                </div>
                <div className="flex-1 pt-2">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials/Social Proof */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-12">
            {t('landing.testimonials.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 shadow-lg"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star} className="text-yellow-400 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">
                  "{t(`landing.testimonials.${i}.text`)}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full flex items-center justify-center text-purple-600 font-semibold">
                    {t(`landing.testimonials.${i}.name`).charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {t(`landing.testimonials.${i}.name`)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {t(`landing.testimonials.${i}.role`)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-purple-600 to-pink-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            {t('landing.cta.title')}
          </h2>
          <p className="text-purple-100 mb-8 text-lg max-w-2xl mx-auto">
            {t('landing.cta.subtitle')}
          </p>
          <button
            onClick={login}
            className="bg-white text-purple-600 px-8 py-4 rounded-xl font-semibold hover:bg-purple-50 transition flex items-center justify-center gap-2 text-lg mx-auto shadow-lg"
          >
            <LogIn className="w-5 h-5" />
            {t('landing.cta.button')}
          </button>
          <p className="text-purple-200 mt-4 text-sm">
            {t('landing.cta.noCreditCard')}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-gray-900 text-gray-400">
        <div className="max-w-6xl mx-auto text-center">
          <img
            src="logo-white.png"
            alt={t('common.appName')}
            className="h-10 mx-auto mb-4"
          />
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm mb-3">
            <a href="/#/support" className="hover:text-white transition">{t('common.support')}</a>
            <a href="/#/privacy" className="hover:text-white transition">{t('common.privacy')}</a>
            <a href="/#/terms" className="hover:text-white transition">{t('common.terms')}</a>
          </div>
          <p className="text-sm">
            {t('landing.footer.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LoginScreen;
